import { useSyncExternalStore } from 'react';

/**
 * Media query that identifies an actual phone: a narrow viewport AND a coarse
 * (touch) pointer. The pointer clause keeps a desktop browser shrunk to a
 * narrow window — or a wide tablet — from being treated as a phone; only true
 * small touch devices match. The 768px ceiling is below tablet width, since the
 * (mobile) shell targets ~390px floor-tools, not tablets.
 */
const PHONE_QUERY = '(max-width: 768px) and (pointer: coarse)';

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {
      // no-op: no matchMedia (SSR / non-browser) — nothing to unsubscribe.
    };
  }
  const mql = window.matchMedia(PHONE_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

function getSnapshot(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(PHONE_QUERY).matches;
}

/**
 * `useIsPhone` — true on small touch devices (phones), reactively.
 *
 * Shell: any. Used to auto-route phones into the (mobile) shell from the
 * desktop `/` entry point. SSR-safe: the server snapshot is always `false`, so
 * the desktop shell is the no-JS / pre-hydration default.
 */
export function useIsPhone(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
